<script setup>
import { computed, nextTick, reactive, ref, watch } from 'vue'
import ViewerIcon from './ViewerIcon.vue'
import AnnotationMediaPreview from './AnnotationMediaPreview.vue'
import { readAnnotationMediaLayout } from '../annotation-media-layout.js'
import {
  compactAnnotationAttachmentPositions,
  createPendingAnnotationAttachment,
  formatAnnotationAttachmentSize,
  inferAnnotationAttachmentKind,
  normalizeAnnotationAttachments,
  normalizeAnnotationTextColor,
  normalizeAnnotationTextSize,
  revokeLocalAnnotationAttachments,
  validateAnnotationAttachmentFiles
} from '../annotation-utils.js'

const props = defineProps({
  open: Boolean,
  mode: {
    type: String,
    default: 'view'
  },
  annotation: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close', 'save', 'delete', 'error'])
const attachmentsContainer = ref(null)
const dragPosition = ref(null)
const dragOverPosition = ref(null)
const originalAttachments = ref([])
const previewAttachment = ref(null)
const previewKind = ref('')
const attachmentMediaLayouts = reactive(new Map())
const annotationAudioElements = new Map()
const annotationAudioPlayback = reactive(new Map())
const emptyAudioPlayback = Object.freeze({ current: 0, duration: 0, playing: false })
const form = reactive({
  title: '',
  text: '',
  titleColor: '#ffffff',
  textColor: '#ffffff',
  textSize: 14,
  attachments: []
})
let pointerDragId = null

const editable = computed(() => props.mode !== 'view')
const heading = computed(() => props.mode === 'create' ? '创建标注' : props.mode === 'edit' ? '编辑标注' : '标注信息')

function cloneAttachments(value) {
  return normalizeAnnotationAttachments(value).map(item => ({ ...item }))
}

function resetForm() {
  stopAnnotationAudio()
  const source = props.annotation || {}
  form.title = props.mode === 'create' ? '' : String(source.title || '')
  form.text = props.mode === 'create' ? '' : String(source.text || '')
  form.titleColor = normalizeAnnotationTextColor(props.mode === 'create' ? '#ffffff' : source.titleColor)
  form.textColor = normalizeAnnotationTextColor(props.mode === 'create' ? '#ffffff' : source.textColor)
  form.textSize = normalizeAnnotationTextSize(props.mode === 'create' ? 14 : source.textSize)
  form.attachments = props.mode === 'create' ? [] : cloneAttachments(source.attachments)
  originalAttachments.value = cloneAttachments(form.attachments)
  attachmentMediaLayouts.clear()
  resetAttachmentDrag()
  closePreview()
}

function cleanupDrafts(preserve = false) {
  const originalUrls = new Set(originalAttachments.value.map(item => item.url))
  const currentUrls = new Set(normalizeAnnotationAttachments(form.attachments).map(item => item.url))
  const candidates = preserve ? originalAttachments.value.filter(item => !currentUrls.has(item.url)) : form.attachments.filter(item => !originalUrls.has(item.url))
  revokeLocalAnnotationAttachments(candidates)
}

function close(preserve = false) {
  stopAnnotationAudio()
  closePreview()
  resetAttachmentDrag()
  cleanupDrafts(preserve)
  emit('close')
}

function submit() {
  emit('save', {
    title: String(form.title || '').trim(),
    text: String(form.text || '').trim(),
    titleColor: normalizeAnnotationTextColor(form.titleColor),
    textColor: normalizeAnnotationTextColor(form.textColor),
    textSize: normalizeAnnotationTextSize(form.textSize),
    attachments: cloneAttachments(form.attachments)
  })
}

function requestDelete() {
  const originalUrls = new Set(originalAttachments.value.map(item => item.url))
  revokeLocalAnnotationAttachments(form.attachments.filter(item => !originalUrls.has(item.url)))
  emit('delete')
}

function handleFiles(event) {
  const input = event.target
  const files = Array.from(input?.files || [])
  if (!files.length) return
  const validationError = validateAnnotationAttachmentFiles(files, normalizeAnnotationAttachments(form.attachments))
  if (validationError) {
    emit('error', validationError)
    input.value = ''
    return
  }
  const pending = files.map(createPendingAnnotationAttachment)
  form.attachments = normalizeAnnotationAttachments([...form.attachments, ...pending])
  input.value = ''
}

function removeAttachment(position) {
  const attachments = cloneAttachments(form.attachments)
  const index = attachments.findIndex(item => item.position === Number(position))
  if (index < 0) return
  const [removed] = attachments.splice(index, 1)
  if (removed?.url && previewAttachment.value?.url === removed.url) closePreview()
  const removedKey = attachmentMediaKey(removed)
  annotationAudioElements.get(removedKey)?.pause?.()
  annotationAudioElements.delete(removedKey)
  annotationAudioPlayback.delete(removedKey)
  attachmentMediaLayouts.delete(removedKey)
  if (String(removed?.url || '').startsWith('blob:')) URL.revokeObjectURL(removed.url)
  form.attachments = compactAnnotationAttachmentPositions(attachments)
}

function attachmentSlots() {
  const byPosition = new Map(cloneAttachments(form.attachments).map(item => [item.position, item]))
  if (form.attachments.length <= 1) {
    return cloneAttachments(form.attachments).map(file => ({
      position: file.position ?? 0,
      file,
      disabled: false
    }))
  }
  return [0, 1, 2].map(position => ({
    position,
    file: byPosition.get(position) || null,
    disabled: false
  }))
}

function resetAttachmentDrag() {
  dragPosition.value = null
  dragOverPosition.value = null
  pointerDragId = null
}

function exchangeAttachmentPositions(fromValue, toValue) {
  const from = Number(fromValue)
  const to = Number(toValue)
  resetAttachmentDrag()
  if (![from, to].every(Number.isInteger) || from === to || to < 0 || to > 2) return
  const attachments = cloneAttachments(form.attachments)
  const fromItem = attachments.find(item => item.position === from)
  const toItem = attachments.find(item => item.position === to)
  if (!fromItem) return
  fromItem.position = to
  if (toItem) toItem.position = from
  if (!toItem && attachments.length === 2 && to !== 0) {
    const otherItem = attachments.find(item => item !== fromItem)
    if (otherItem) otherItem.position = 0
  }
  form.attachments = normalizeAnnotationAttachments(attachments)
}

function attachmentPositionAt(clientX, clientY) {
  const element = document.elementFromPoint?.(clientX, clientY)
  const card = element?.closest?.('.annotation-attachment[data-attachment-position]')
  if (!card || !attachmentsContainer.value?.contains(card) || card.dataset.disabled === 'true') return null
  const position = Number(card.dataset.attachmentPosition)
  return Number.isInteger(position) && position >= 0 && position <= 2 ? position : null
}

function pointerDragStart(position, event) {
  if (!editable.value || form.attachments.length <= 1 || event?.button > 0) return
  event?.preventDefault?.()
  event?.stopPropagation?.()
  dragPosition.value = Number(position)
  dragOverPosition.value = Number(position)
  pointerDragId = event.pointerId
  event.currentTarget?.setPointerCapture?.(event.pointerId)
}

function pointerDragMove(event) {
  if (pointerDragId === null || event.pointerId !== pointerDragId) return
  event.preventDefault?.()
  const position = attachmentPositionAt(event.clientX, event.clientY)
  dragOverPosition.value = position
}

function pointerDragEnd(event, cancelled = false) {
  if (pointerDragId === null || event.pointerId !== pointerDragId) return
  event.preventDefault?.()
  const from = dragPosition.value
  const to = cancelled ? null : attachmentPositionAt(event.clientX, event.clientY)
  if (event.currentTarget?.hasPointerCapture?.(event.pointerId)) {
    event.currentTarget.releasePointerCapture(event.pointerId)
  }
  if (to === null) {
    resetAttachmentDrag()
    return
  }
  exchangeAttachmentPositions(from, to)
}

function dragStart(position, event) {
  dragPosition.value = Number(position)
  event?.dataTransfer?.setData?.('text/plain', String(position))
  if (event?.dataTransfer) event.dataTransfer.effectAllowed = 'move'
}

function drop(position, event) {
  event?.preventDefault?.()
  const from = Number(dragPosition.value ?? event?.dataTransfer?.getData?.('text/plain'))
  exchangeAttachmentPositions(from, position)
}

function attachmentKind(file) {
  return inferAnnotationAttachmentKind(file?.kind, file?.mime, file?.name, file?.url)
}

function attachmentMediaKey(file) {
  return String(file?.url || file?.fileId || file?.storageKey || file?.name || '')
}

function formatAudioTime(value) {
  const seconds = Math.max(0, Math.floor(Number(value) || 0))
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

function audioPlaybackFor(file) {
  return annotationAudioPlayback.get(attachmentMediaKey(file)) || emptyAudioPlayback
}

function audioProgressFor(file) {
  const state = audioPlaybackFor(file)
  const progress = state.duration > 0 ? Math.min(1, state.current / state.duration) : 0
  return { '--annotation-audio-progress': `${Math.round(progress * 100)}%` }
}

function audioTimeFor(file) {
  const state = audioPlaybackFor(file)
  return formatAudioTime(state.playing && state.current > 0 ? state.current : state.duration)
}

function setAnnotationAudioRef(file, element) {
  const key = attachmentMediaKey(file)
  if (!key) return
  if (element) annotationAudioElements.set(key, element)
  else annotationAudioElements.delete(key)
}

function updateAnnotationAudio(file, event, patch = {}) {
  const key = attachmentMediaKey(file)
  const audio = event?.currentTarget
  if (!key) return
  const previous = annotationAudioPlayback.get(key) || emptyAudioPlayback
  annotationAudioPlayback.set(key, {
    current: Number.isFinite(audio?.currentTime) ? audio.currentTime : previous.current,
    duration: Number.isFinite(audio?.duration) ? audio.duration : previous.duration,
    playing: previous.playing,
    ...patch
  })
}

function stopAnnotationAudio(exceptKey = '') {
  annotationAudioElements.forEach((audio, key) => {
    if (key === exceptKey) return
    audio.pause?.()
    const previous = annotationAudioPlayback.get(key)
    if (previous) annotationAudioPlayback.set(key, { ...previous, playing: false })
  })
}

function toggleAnnotationAudio(file) {
  const key = attachmentMediaKey(file)
  const audio = annotationAudioElements.get(key)
  if (!audio) return
  if (!audio.paused) {
    audio.pause()
    return
  }
  stopAnnotationAudio(key)
  if (audio.ended) audio.currentTime = 0
  const result = audio.play?.()
  result?.catch?.(() => {
    const previous = annotationAudioPlayback.get(key) || emptyAudioPlayback
    annotationAudioPlayback.set(key, { ...previous, playing: false })
  })
}

function captureAttachmentMediaLayout(file, event) {
  const key = attachmentMediaKey(file)
  const layout = readAnnotationMediaLayout(event?.currentTarget)
  if (key && layout) attachmentMediaLayouts.set(key, layout)
}

function attachmentMediaClass(file) {
  const layout = attachmentMediaLayouts.get(attachmentMediaKey(file))
  return layout ? `is-media-${layout.orientation}` : 'is-media-pending'
}

function attachmentMediaStyle(file) {
  const layout = attachmentMediaLayouts.get(attachmentMediaKey(file))
  if (!layout) return null
  return {
    '--annotation-media-ratio': String(layout.ratio),
    '--annotation-media-width': `${Math.min(240, layout.ratio * 260)}px`,
    '--annotation-media-grid-width': `${Math.min(150, layout.ratio * 210)}px`
  }
}

function preview(file) {
  const kind = attachmentKind(file)
  if (!['image', 'video'].includes(kind) || !file?.url) return
  previewAttachment.value = file
  previewKind.value = kind
}

function closePreview() {
  previewAttachment.value = null
  previewKind.value = ''
}

watch(() => [props.open, props.mode, props.annotation?.id], ([open]) => {
  if (open) nextTick(resetForm)
  else stopAnnotationAudio()
}, { immediate: true })
</script>

<template>
  <Transition name="annotation-sheet">
  <div v-if="open" class="annotation-dialog-layer" @pointerdown.self="editable ? null : close()">
    <section class="annotation-dialog" role="dialog" aria-modal="true" :aria-label="heading" @pointerdown.stop>
      <header class="annotation-dialog__header">
        <div class="annotation-dialog__heading">
          <span class="annotation-dialog__mark" aria-hidden="true"><i></i></span>
          <div>
            <h2>{{ heading }}</h2>
            <small>{{ editable ? '为画面补充一段说明' : '来自场景中的空间标注' }}</small>
          </div>
        </div>
        <button type="button" aria-label="关闭" @click="close()">×</button>
      </header>

      <div class="annotation-dialog__body">
        <template v-if="!editable">
          <div class="annotation-view-card">
            <span class="annotation-view-card__eyebrow"><i></i>标注内容</span>
            <strong :style="{ color: form.titleColor, fontSize: `${normalizeAnnotationTextSize(form.textSize) + 2}px` }">{{ form.title || '未命名标注' }}</strong>
            <p :style="{ color: form.textColor, fontSize: `${normalizeAnnotationTextSize(form.textSize)}px` }">{{ form.text || '暂无内容' }}</p>
          </div>
        </template>

        <template v-else>
          <label class="annotation-field">
            <span>标题</span>
            <input v-model="form.title" maxlength="120" placeholder="输入标注标题" />
          </label>
          <label class="annotation-field">
            <span>内容</span>
            <textarea v-model="form.text" rows="3" maxlength="4000" placeholder="输入标注内容"></textarea>
          </label>
          <div class="annotation-style-row">
            <label class="annotation-color-field">
              <span>标题颜色</span>
              <div><input v-model="form.titleColor" type="color" /><b>{{ normalizeAnnotationTextColor(form.titleColor).toUpperCase() }}</b></div>
            </label>
            <label class="annotation-color-field">
              <span>正文颜色</span>
              <div><input v-model="form.textColor" type="color" /><b>{{ normalizeAnnotationTextColor(form.textColor).toUpperCase() }}</b></div>
            </label>
            <label class="annotation-size-field">
              <span>正文大小</span>
              <select v-model.number="form.textSize">
                <option v-for="size in [12,14,16,18,20,24,28,32]" :key="size" :value="size">{{ size }} px</option>
              </select>
              <small>标题 {{ normalizeAnnotationTextSize(form.textSize) + 2 }} px</small>
            </label>
          </div>
          <div class="annotation-field annotation-field--attachments">
            <span>附件</span>
            <div class="annotation-attachment-pickers">
              <label class="annotation-file-choice" :class="{ 'is-disabled': form.attachments.length >= 3 }">
                <input
                  class="annotation-file-input"
                  type="file"
                  multiple
                  accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
                  :disabled="form.attachments.length >= 3"
                  aria-label="选择标注图片"
                  @change="handleFiles"
                />
                <ViewerIcon name="image" /><b>图片</b>
              </label>
              <label class="annotation-file-choice" :class="{ 'is-disabled': form.attachments.length >= 3 }">
                <input
                  class="annotation-file-input"
                  type="file"
                  accept="video/*,.mp4,.mov,.m4v,.webm"
                  :disabled="form.attachments.length >= 3"
                  aria-label="选择标注视频"
                  @change="handleFiles"
                />
                <ViewerIcon name="video" /><b>视频</b>
              </label>
              <label class="annotation-file-choice" :class="{ 'is-disabled': form.attachments.length >= 3 }">
                <input
                  class="annotation-file-input"
                  type="file"
                  accept="audio/*,.mp3,.wav,.m4a,.aac,.ogg,.flac,.opus"
                  :disabled="form.attachments.length >= 3"
                  aria-label="选择标注音频"
                  @change="handleFiles"
                />
                <ViewerIcon name="audio" /><b>音频</b>
              </label>
            </div>
            <small>分别选择附件，最多 3 个；图片 10 MB、音频 20 MB、视频 100 MB</small>
          </div>
        </template>

        <div
          v-if="form.attachments.length || editable"
          ref="attachmentsContainer"
          class="annotation-attachments"
          :class="{
            'is-editable': editable,
            'is-single': form.attachments.length === 1,
            'is-slot-grid': form.attachments.length > 1
          }"
        >
          <article
            v-for="slot in (editable ? attachmentSlots() : cloneAttachments(form.attachments).map(file => ({ position: file.position, file })))"
            v-show="slot.file || dragPosition !== null"
            :key="`${slot.position}-${slot.file?.url || 'empty'}`"
            class="annotation-attachment"
            :data-attachment-position="slot.position"
            :data-disabled="slot.disabled ? 'true' : 'false'"
            :style="form.attachments.length > 1 ? { gridColumn: String((slot.position % 2) + 1), gridRow: String(Math.floor(slot.position / 2) + 1) } : null"
            :class="{
              'is-empty': !slot.file,
              'is-disabled': slot.disabled,
              'is-dragging': dragPosition === slot.position,
              'is-drop-target': dragOverPosition === slot.position && dragPosition !== slot.position,
              'is-image': slot.file && attachmentKind(slot.file) === 'image',
              'is-video': slot.file && attachmentKind(slot.file) === 'video',
              'is-audio': slot.file && attachmentKind(slot.file) === 'audio'
            }"
            :draggable="editable && !!slot.file && form.attachments.length > 1"
            @dragstart="dragStart(slot.position, $event)"
            @dragover.prevent="!slot.disabled && (dragOverPosition = slot.position)"
            @dragleave="dragOverPosition === slot.position && (dragOverPosition = null)"
            @drop="!slot.disabled && drop(slot.position, $event)"
            @dragend="resetAttachmentDrag"
          >
            <template v-if="slot.file">
              <div v-if="editable && form.attachments.length > 1" class="annotation-attachment__reorder">
                <span>位置 {{ slot.position + 1 }}</span>
                <button
                  type="button"
                  draggable="false"
                  :aria-label="`拖动“${slot.file.name || '附件'}”交换位置`"
                  @pointerdown="pointerDragStart(slot.position, $event)"
                  @pointermove="pointerDragMove"
                  @pointerup="pointerDragEnd"
                  @pointercancel="pointerDragEnd($event, true)"
                >
                  <ViewerIcon name="drag-grip" />
                </button>
              </div>
              <button
                v-if="['image', 'video'].includes(attachmentKind(slot.file))"
                type="button"
                class="annotation-attachment__media"
                :class="[`is-${attachmentKind(slot.file)}`, attachmentMediaClass(slot.file)]"
                :style="attachmentMediaStyle(slot.file)"
                :aria-label="`预览${attachmentKind(slot.file) === 'video' ? '视频' : '图片'} ${slot.file.name || ''}`"
                @click="preview(slot.file)"
              >
                <img
                  v-if="attachmentKind(slot.file) === 'image'"
                  :src="slot.file.url"
                  :alt="slot.file.name"
                  @load="captureAttachmentMediaLayout(slot.file, $event)"
                />
                <video
                  v-else
                  :src="slot.file.url"
                  muted
                  playsinline
                  preload="metadata"
                  @loadedmetadata="captureAttachmentMediaLayout(slot.file, $event)"
                ></video>
                <span v-if="attachmentKind(slot.file) === 'video'" class="annotation-attachment__play" aria-hidden="true"></span>
              </button>
              <div v-else-if="attachmentKind(slot.file) === 'audio'" class="annotation-attachment__audio">
                <button
                  type="button"
                  class="annotation-attachment__audio-message"
                  :class="{ 'is-playing': audioPlaybackFor(slot.file).playing }"
                  :style="audioProgressFor(slot.file)"
                  :aria-label="audioPlaybackFor(slot.file).playing ? '暂停录音' : '播放录音'"
                  @click="toggleAnnotationAudio(slot.file)"
                >
                  <span class="annotation-attachment__audio-toggle" aria-hidden="true"></span>
                  <span class="annotation-attachment__audio-wave" aria-hidden="true">
                    <i v-for="bar in 18" :key="bar"></i>
                  </span>
                  <time>{{ audioTimeFor(slot.file) }}</time>
                </button>
                <audio
                  :ref="element => setAnnotationAudioRef(slot.file, element)"
                  :src="slot.file.url"
                  preload="metadata"
                  @loadedmetadata="updateAnnotationAudio(slot.file, $event)"
                  @timeupdate="updateAnnotationAudio(slot.file, $event)"
                  @play="updateAnnotationAudio(slot.file, $event, { playing: true })"
                  @pause="updateAnnotationAudio(slot.file, $event, { playing: false })"
                  @ended="updateAnnotationAudio(slot.file, $event, { playing: false, current: 0 })"
                ></audio>
              </div>
              <a v-else :href="slot.file.url" target="_blank" rel="noopener noreferrer">{{ slot.file.name }}</a>
              <footer>
                <span class="annotation-attachment__meta">{{ slot.file.name }} · {{ formatAnnotationAttachmentSize(slot.file.size) }}</span>
                <span class="annotation-attachment__actions">
                  <button v-if="['image','video'].includes(attachmentKind(slot.file))" type="button" @click="preview(slot.file)">预览</button>
                  <button v-if="editable" type="button" @click="removeAttachment(slot.position)">删除</button>
                </span>
              </footer>
            </template>
            <div v-else class="annotation-attachment__empty" aria-hidden="true">拖到这里</div>
          </article>
        </div>
      </div>

      <footer class="annotation-dialog__footer" :class="{ 'has-destructive': mode === 'edit' && editable }">
        <button type="button" @click="close()">{{ editable ? '取消' : '确定' }}</button>
        <button v-if="mode === 'edit'" class="is-danger" type="button" @click="requestDelete">删除</button>
        <button v-if="editable" class="is-primary" type="button" @click="submit">保存</button>
      </footer>
    </section>
    <AnnotationMediaPreview
      :attachment="previewAttachment"
      :kind="previewKind"
      :media-layout="attachmentMediaLayouts.get(attachmentMediaKey(previewAttachment)) || null"
      @close="closePreview"
    />
  </div>
  </Transition>
</template>
